// frontend/src/pages/Account/AccountDocumentationPage.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { listDocuments, uploadDocument, getDownloadUrl, deleteDocument } from '../../services/documentService';
import { useAuth } from '../../contexts/AuthContext';
import styles from './AccountDocumentationPage.module.css';
import Button from '../../components/Button/Button';

// ─── Helpers ──────────────────────────────────────────────────────────
const isReceipt = (doc) =>
    doc.description && doc.description.includes('Payment Receipt');

const isSignedAgreement = (doc) =>
    (doc.fileName && doc.fileName.startsWith('Signed:')) ||
    (doc.description && doc.description.includes('Signed Rental Agreement'));

const isAboutHighview = (doc) =>
    doc.isGlobal && !isReceipt(doc) && !isSignedAgreement(doc);

function categorizeDocuments(documents, currentUserId) {
    const receipts = [];
    const signedAgreements = [];
    const aboutHighview = [];
    const myDocs = [];

    documents.forEach((doc) => {
        if (isReceipt(doc)) receipts.push(doc);
        else if (isSignedAgreement(doc)) signedAgreements.push(doc);
        else if (isAboutHighview(doc)) aboutHighview.push(doc);
        else myDocs.push(doc);
    });

    return { receipts, signedAgreements, aboutHighview, myDocs };
}

// ─── Category Section Component ───────────────────────────────────────
const CategorySection = ({
    title,
    docs,
    defaultOpen = true,
    onView,
    onDelete,
    canDelete,
}) => {
    const [open, setOpen] = useState(defaultOpen);

    return (
        <div className={styles.categorySection}>
            <button
                className={styles.categoryHeader}
                onClick={() => setOpen(!open)}
                type="button"
            >
                <span className={styles.categoryTitle}>
                    {title}
                    <span className={styles.categoryCount}>{docs.length}</span>
                </span>
                <span className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`}>
                    ▸
                </span>
            </button>

            {open && (
                <div className={styles.categoryBody}>
                    {docs.length === 0 ? (
                        <p className={styles.emptyMessage}>No documents in this category.</p>
                    ) : (
                        docs.map((doc) => (
                            <div key={doc.id} className={styles.docCard}>
                                <div className={styles.docInfo}>
                                    <span className={styles.docFileName}>{doc.fileName}</span>
                                    {doc.description && (
                                        <span className={styles.docDescription}>{doc.description}</span>
                                    )}
                                    <span className={styles.docMeta}>
                                        {doc.uploader ? doc.uploader.username : 'System'} &middot;{' '}
                                        {new Date(doc.createdAt).toLocaleDateString()} &middot;{' '}
                                        {(doc.fileSize / 1024).toFixed(0)} KB
                                    </span>
                                </div>
                                <div className={styles.docActions}>
                                    <Button
                                        onClick={() => onView(doc.id, doc.fileName)}
                                        variant="secondary"
                                        size="sm"
                                    >
                                        View
                                    </Button>
                                    {canDelete(doc) && (
                                        <Button
                                            onClick={() => onDelete(doc.id, doc.fileName)}
                                            variant="secondary"
                                            size="sm"
                                        >
                                            Delete
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

// ─── Main Page ────────────────────────────────────────────────────────
const AccountDocumentationPage = () => {
    const { currentUser } = useAuth();
    const isAdmin = currentUser?.role === 'Administrator';

    const [documents, setDocuments] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Upload state
    const [selectedFile, setSelectedFile] = useState(null);
    const [description, setDescription] = useState('');
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [uploadSuccess, setUploadSuccess] = useState('');
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef(null);

    // Delete feedback
    const [deleteError, setDeleteError] = useState(null);
    const [deleteSuccess, setDeleteSuccess] = useState('');

    // ─── Data Fetching ────────────────────────────────────────────────
    const fetchDocuments = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await listDocuments();
            setDocuments(data || []);
        } catch (err) {
            setError(err.message || 'Failed to load documents.');
            setDocuments([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDocuments();
    }, [fetchDocuments]);

    // ─── Upload Logic ─────────────────────────────────────────────────
    const handleFileSelect = (file) => {
        if (file && file.type === 'application/pdf') {
            setSelectedFile(file);
            setUploadError(null);
            setUploadSuccess('');
        } else if (file) {
            setUploadError('Only PDF files are accepted.');
        }
    };

    const handleFileChange = (e) => handleFileSelect(e.target.files[0]);

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
        else if (e.type === 'dragleave') setDragActive(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFileSelect(e.dataTransfer.files[0]);
        }
    };

    const handleSubmitUpload = async (e) => {
        e.preventDefault();
        if (!selectedFile) { setUploadError('Please select a file.'); return; }
        if (!description.trim()) { setUploadError('Please enter a description.'); return; }

        const formData = new FormData();
        formData.append('documentFile', selectedFile);
        formData.append('description', description);

        setUploading(true);
        setUploadError(null);
        setUploadSuccess('');

        try {
            const response = await uploadDocument(formData);
            setUploadSuccess(response.message || 'Document uploaded successfully!');
            setSelectedFile(null);
            setDescription('');
            if (fileInputRef.current) fileInputRef.current.value = null;
            fetchDocuments();
            setTimeout(() => setUploadSuccess(''), 5000);
        } catch (err) {
            setUploadError(err.message || 'Upload failed. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    // ─── View / Delete ────────────────────────────────────────────────
    const handleView = async (documentId, originalFileName) => {
        try {
            const { downloadUrl } = await getDownloadUrl(documentId);
            window.open(downloadUrl, '_blank', 'noopener,noreferrer');
        } catch (err) {
            alert(`Failed to open document: ${err.message || 'Unknown error'}`);
        }
    };

    const handleDelete = async (documentId, fileName) => {
        setDeleteError(null);
        setDeleteSuccess('');
        if (!window.confirm(`Are you sure you want to delete "${fileName}"? This action cannot be undone.`)) return;

        try {
            const response = await deleteDocument(documentId);
            setDeleteSuccess(response.message || 'Document deleted successfully!');
            fetchDocuments();
            setTimeout(() => setDeleteSuccess(''), 3000);
        } catch (err) {
            setDeleteError(err.message || 'Failed to delete document.');
            setTimeout(() => setDeleteError(null), 5000);
        }
    };

    // Permission check: can the current user delete this document?
    // Admins should use the Admin Document Management page to delete — not this page.
    const canDelete = (doc) => {
        // No one can delete receipts, signed agreements, or About Highview House docs from this page
        if (isReceipt(doc) || isSignedAgreement(doc) || isAboutHighview(doc)) return false;
        // Users (including admins) can only delete their own uploads from this page
        return doc.uploadedByUserId === currentUser?.id;
    };

    // ─── Render ───────────────────────────────────────────────────────
    if (isLoading && documents.length === 0 && !error) {
        return <p className={styles.loadingMessage}>Loading documents...</p>;
    }

    const { receipts, signedAgreements, aboutHighview, myDocs } = categorizeDocuments(
        documents,
        currentUser?.id
    );

    return (
        <div className={styles.documentationPageContainer}>
            <h2 className="page-main-title">Account Documentation</h2>

            {/* Feedback Messages */}
            {deleteError && <p className={styles.errorMessage}>Delete Error: {deleteError}</p>}
            {deleteSuccess && <p className={styles.successMessage}>{deleteSuccess}</p>}
            {error && !isLoading && <p className={styles.errorMessage}>Error: {error}</p>}

            {/* Categorized Document Sections */}
            <div className={styles.categoriesContainer}>
                <CategorySection
                    title="Receipts"
                    docs={receipts}
                    onView={handleView}
                    onDelete={handleDelete}
                    canDelete={canDelete}
                />
                <CategorySection
                    title="Signed Agreements"
                    docs={signedAgreements}
                    onView={handleView}
                    onDelete={handleDelete}
                    canDelete={canDelete}
                />
                <CategorySection
                    title="About Highview House"
                    docs={aboutHighview}
                    onView={handleView}
                    onDelete={handleDelete}
                    canDelete={canDelete}
                />
                <CategorySection
                    title="My Documents & Other"
                    icon=""
                    docs={myDocs}
                    onView={handleView}
                    onDelete={handleDelete}
                    canDelete={canDelete}
                />
            </div>

            {isLoading && documents.length > 0 && !error && (
                <p className={styles.loadingMessage}>Refreshing documents...</p>
            )}

            {/* Upload Section — at the bottom */}
            <div className={styles.uploadSection}>
                <h3>Add a Document</h3>
                <form onSubmit={handleSubmitUpload}>
                    {/* Drop Zone */}
                    <div
                        className={`${styles.dropZone} ${dragActive ? styles.dropZoneActive : ''} ${selectedFile ? styles.dropZoneHasFile : ''}`}
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf"
                            onChange={handleFileChange}
                            disabled={uploading}
                            className={styles.hiddenFileInput}
                        />
                        {selectedFile ? (
                            <div className={styles.dropZoneContent}>
                                <span className={styles.dropZoneIcon}>✓</span>
                                <span className={styles.dropZoneText}>{selectedFile.name}</span>
                                <span className={styles.dropZoneHint}>Click to change file</span>
                            </div>
                        ) : (
                            <div className={styles.dropZoneContent}>
                                <span className={styles.dropZoneIcon}>+</span>
                                <span className={styles.dropZoneText}>Click or drag to add a file</span>
                                <span className={styles.dropZoneHint}>PDF files only, up to 10 MB</span>
                            </div>
                        )}
                    </div>

                    {/* Description */}
                    <div className={styles.formGroup}>
                        <label htmlFor="descriptionInput">Description</label>
                        <input
                            type="text"
                            id="descriptionInput"
                            value={description}
                            onChange={(e) => { setDescription(e.target.value); setUploadError(null); }}
                            placeholder="Brief description of the document"
                            disabled={uploading}
                            required
                        />
                    </div>

                    <Button
                        type="submit"
                        disabled={uploading || !selectedFile || !description.trim()}
                        className={styles.uploadButton}
                        variant="primary"
                    >
                        {uploading ? 'Uploading...' : 'Upload Document'}
                    </Button>
                </form>
                {uploadError && <p className={styles.errorMessage}>{uploadError}</p>}
                {uploadSuccess && <p className={styles.successMessage}>{uploadSuccess}</p>}
            </div>
        </div>
    );
};

export default AccountDocumentationPage;